import { Component, Inject } from '@angular/core';
import {
  DwComponent,
  DwComponentType,
  DwDataSort,
  DwEventService,
  DwMetaDataFilterTileState,
  DwMetaDataGridActionState,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwQueryFilterGroup,
  DwRoutingService
} from '@devwareapps/devware-cap';

import {
  AppMetaDataItemNames,
  StudentModuleLessonActivityQueryEntity
} from '../../../../meta-data/app-meta-data.service';
import { ShowQuizResultModalActionService } from '../../../quizzes/actions/show-quiz-result-modal-action.service';
import { AdminManagerService } from '../../../admin/services/admin-manager.service';
import { DwAviatorGridRendererBaseComponent } from '../../../shared/components/grids/dw-aviator-grid-renderer-base.component';
import { DateTimeUtilService } from '../../../shared/util/date-time-util.service';

interface StudentModuleLessonActivityViewState {
  readonly completed: boolean;
  readonly sessionStart: string;
  readonly duration: string;
  readonly result: string;
  readonly resultClass: string;
  readonly score: string;
  readonly hasQuizResult: boolean;
}

interface StudentModuleLessonActivityGridRow extends StudentModuleLessonActivityQueryEntity {
  readonly viewState: StudentModuleLessonActivityViewState;
}

@DwComponent({
  key: 'student-module-lesson-activity-grid-renderer',
  name: 'Student Module Lesson Activity Grid Renderer',
  isGlobal: false,
  parentItemName: AppMetaDataItemNames.CourseModuleProgress,
  componentType: DwComponentType.gridRenderer
})
@Component({
  selector: 'app-student-module-lesson-activity-grid-renderer',
  templateUrl: './student-module-lesson-activity-grid-renderer.component.html',
  styleUrls: ['./student-module-lesson-activity-grid-renderer.component.scss']
})
export class StudentModuleLessonActivityGridRendererComponent
  extends DwAviatorGridRendererBaseComponent<StudentModuleLessonActivityGridRow> {

  public sortDirection: 'asc' | 'desc' = 'desc';

  private readonly sessionStartSortField = 'CourseLessonSession.CourseLessonSessionSessionStartDateTime';

  constructor(
    dwRoutingService: DwRoutingService,
    adminManagerService: AdminManagerService,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwEventService: DwEventService,
    private readonly dateTimeUtilService: DateTimeUtilService,
    private readonly showQuizResultModalActionService: ShowQuizResultModalActionService
  ) {
    super(dwRoutingService, adminManagerService, dwMetaDataService, dwEventService);
  }

  public setupRenderer(): void { }

  public customFiltersChanged(): void {
    this.applyCustomFilters(new DwQueryFilterGroup());
  }

  public toggleSessionStartSort(): void {
    this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
    this.setSorts(this.getEffectiveSorts());
  }

  public showQuizDetails(row: StudentModuleLessonActivityGridRow, event: MouseEvent): void {
    event.stopPropagation();

    if (!row.viewState.hasQuizResult) {
      return;
    }

    this.showQuizResultModalActionService.handleAction({
      data: row
    } as DwMetaDataGridActionState<StudentModuleLessonActivityQueryEntity>).subscribe(result => {
      if ((result as any)?.refreshData) {
        this.refreshDataSource();
      }
    });
  }

  public onScroll(): void {
    this.nextPage();
  }

  public trackByActivity(index: number, row: StudentModuleLessonActivityGridRow): string | number {
    return row.CourseLessonSessionCourseLessonSessionId
      || row.QuizResultQuizResultId
      || `${row.CourseLessonProgressCourseLessonId || 'lesson'}-${row.CourseLessonSessionSessionStartDateTime || index}`;
  }

  protected filterTileStateChange(filterTileState: DwMetaDataFilterTileState): void { }

  protected resolveInitialPageSize(): number {
    return 50;
  }

  protected getEffectiveSorts(): DwDataSort[] {
    return [{
      fieldName: this.sessionStartSortField,
      ascending: this.sortDirection === 'asc'
    } as DwDataSort];
  }

  protected mapResults(results: any[]): StudentModuleLessonActivityGridRow[] {
    return ((results || []) as StudentModuleLessonActivityQueryEntity[]).map(row => ({
      ...row,
      viewState: this.buildViewState(row)
    }));
  }

  private buildViewState(row: StudentModuleLessonActivityQueryEntity): StudentModuleLessonActivityViewState {
    const result = row.QuizResultQuizResultStatusIdDisplay || '';
    const hasQuizResult = !!(row.QuizResultQuizResultId || row.CourseLessonSessionQuizResultId);
    const score = row.QuizResultPercentageScore != null
      ? row.QuizResultPercentageScore
      : row.QuizResultQuizScore;

    return {
      completed: !!row.CourseLessonSessionLessonCompleted,
      sessionStart: row.CourseLessonSessionSessionStartDateTime
        ? this.dateTimeUtilService.formatDateTimeToLocal(row.CourseLessonSessionSessionStartDateTime, 'MM/dd/yyyy HH:mm')
        : '-',
      duration: this.formatDuration(row.CourseLessonSessionTotalTimeSeconds),
      result: result || '-',
      resultClass: this.getResultClass(result),
      score: score != null ? Math.round(score).toString() : '-',
      hasQuizResult
    };
  }

  private formatDuration(seconds?: number): string {
    if (seconds == null) {
      return '-';
    }

    return this.dateTimeUtilService.formatDurationMinutesSeconds(seconds) || '0:00';
  }

  private getResultClass(result: string): string {
    const normalizedResult = (result || '').toLowerCase();

    if (normalizedResult.includes('pass') || normalizedResult.includes('complete')) {
      return 'activity-grid__result--success';
    }

    if (normalizedResult.includes('fail')) {
      return 'activity-grid__result--danger';
    }

    return normalizedResult ? 'activity-grid__result--neutral' : '';
  }
}
